import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { LoaderService } from '../loader/loader.service';
import {
  ImageCropperComponent,
  ImageCropperModule,
  ImageTransform
} from 'ngx-image-cropper';
import {
  CommonBindingDataService,
  NotificationService
} from '@Shared/services';
import { ImageCroppedEvent } from '@App/common/interfaces';
import { BusinessService, UserCommonApiService } from '@App/common/services';
import { UploadFileTypesEnum } from '@App/common/enums';
import {
  file_extensions_pdf_doc,
  heic_extensions,
  image_extensions,
  video_extensions
} from '@Shared/mock/file-types.mock';
import {
  ToggleSwitchChangeEvent,
  ToggleSwitchModule
} from 'primeng/toggleswitch';
import { Subject, takeWhile } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { SliderModule } from 'primeng/slider';
import { FormsModule } from '@angular/forms';
import { isPlatformBrowser, NgStyle, NgIf } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { NoLeadingSpaceDirective } from '@App/common/directives';
import { ImageCropperDialogComponent } from '../image-cropper-dialog/image-cropper-dialog.component';

@Component({
  selector: 'app-file-uploader',
  templateUrl: './uploader-image-file.component.html',
  styleUrls: ['./uploader-image-file.component.scss'],
  standalone: true,
  imports: [
    NoLeadingSpaceDirective,
    DialogModule,
    ToggleSwitchModule,
    FormsModule,
    SliderModule,
    ImageCropperDialogComponent,
    TranslateModule,
    ImageCropperModule
  ]
})
export class UploaderImageFileComponent implements OnInit, OnDestroy {
  @Input() accept: any;
  @Input() multipleUpload: boolean;
  @Input() uploadButtonLabel: any;
  @Input() maintainAspectRatio = true;
  @Input() imageQuality = 92;
  @Input() resizeToWidth = 250;
  @Input() resizeToHeight = 250;
  @Input() cropperMinHeight = 250;
  @Input() cropperMinWidth = 250;
  @Input() aspectRatio = 4 / 3;
  @Input() isCropperActive: boolean;
  @Input() dialogWidth = '400px';
  @Input() onlyScaleDown = true;
  @Input() isSwitcherCropperActive!: boolean;
  @Input() containWithinAspectRatio: boolean;
  @Input() isRoundCropper: boolean;
  @Output() beforeFileUploadEvent: EventEmitter<any> = new EventEmitter();
  @Output() afterFileUploadEvent: EventEmitter<any> = new EventEmitter();
  @Output() errorInImageCropperUpload: EventEmitter<any> = new EventEmitter();
  imageURL: string = '';
  croppedImage: any = '';
  showCropper = false;
  displayCropBtn: boolean = false;
  eventTemp: any;
  transform: ImageTransform = { scale: 1 };
  zoomValue = 1;
  @ViewChild(ImageCropperComponent)
  imageCropper?: ImageCropperComponent;
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @Input() acceptTypes!: string;
  @Input() unlimitedDuration: boolean;
  minZoom: number = 0.01;
  maxZoom: number = 2;
  private croppingImageData: { name: string; type: string };
  private uploadingVideoId: string;
  private destroy$: Subject<void> = new Subject();
  private isBrowser: boolean;

  constructor(
    private loaderService: LoaderService,
    private userCommonService: UserCommonApiService,
    private notificationService: NotificationService,
    private businessService: BusinessService,
    private commonBindingService: CommonBindingDataService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  private _isZeroZoom: boolean;

  @Input() set isZeroZoom(value: boolean) {
    this._isZeroZoom = value;
    if (this._isZeroZoom) {
      this.transform = { scale: 0 };
      this.zoomValue = 0;
      this.minZoom = 1;
    }
  }

  _displayImageCropper = false;

  get displayImageCropper(): boolean {
    return this._displayImageCropper;
  }

  set displayImageCropper(value: boolean) {
    if (!value) {
      this.clearInput();
    }
    this._displayImageCropper = value;
  }

  ngOnInit() {}

  open(): void {
    this.fileInput?.nativeElement?.click();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUpload(event) {
    this.beforeFileUploadEvent.emit(event);
    this.afterFileUploadEvent.emit(event);
    this.loaderService.hide();
  }

  fileChangeEvent(event: any) {
    this.loaderService.show();
    Array.from(event.target.files).map((file: any) => {
      const maxSize = 500 * 1024 * 1024;
      if (file.size > maxSize) {
        this.loaderService.hide();
        this.notificationService.showNotification(
          'ERROR',
          'File size should not exceed 500MB'
        );
        this.clearInput();
        return;
      }

      const extension = file.name.split('.').pop().toLowerCase();
      if (heic_extensions.some((ext) => ext === extension)) {
        this.convertHeicToJpeg(file).then((jpegBlob) => {
          const jpegFile = new File(
            [jpegBlob],
            file.name.replace(/\.(heic|heif|jfif)$/i, '.jpeg'),
            { type: 'image/jpeg' }
          );
          if (this.isCropperActive) {
            this.loaderService.hide();
            if (isPlatformBrowser(this.platformId)) {
              this.imageCropperLoad(
                window.URL.createObjectURL(jpegFile),
                jpegFile
              );
            }
          } else {
            this.uploadFileWorkAround(jpegFile, 0);
          }
        });
      } else if (image_extensions.some((ext) => ext === extension)) {
        if (this.isCropperActive) {
          this.loaderService.hide();
          if (isPlatformBrowser(this.platformId)) {
            this.imageCropperLoad(window.URL.createObjectURL(file), file);
          }
        } else {
          this.uploadFileWorkAround(file, 0);
        }
      } else if (file_extensions_pdf_doc.some((ext) => ext === extension)) {
        switch (extension) {
          case 'pdf':
            this.uploadFileWorkAround(file, 2);
            break;
          case 'doc':
            this.uploadFileWorkAround(file, 3);
            break;
          case 'docx':
            this.uploadFileWorkAround(file, 4);
            break;
          default:
            this.loaderService.hide();
            this.notificationService.showNotification(
              'ERROR',
              'This format is not supported!'
            );
            break;
        }
      } else if (
        video_extensions.some(
          (ext) => ext.toLowerCase() === '.' + extension.toLowerCase()
        )
      ) {
        const video = isPlatformBrowser(this.platformId)
          ? document.createElement('video')
          : null;
        let duration = 0;
        if (video) {
          video.preload = 'metadata';
          video.onloadedmetadata = () => {
            duration = video.duration;
            if (this.unlimitedDuration || Math.round(duration)) {
              this.uploadVideoFile(file);
            } else {
              this.loaderService.hide();
              this.notificationService.showNotification(
                'ERROR',
                'Video duration should be less than 59 seconds, your video duration is ' +
                  duration.toFixed(1) +
                  ' seconds'
              );
            }
            video.remove();
          };
          video.src = URL.createObjectURL(file);
        }
      } else {
        this.notificationService.showNotification(
          'ERROR',
          'This format is not supported!'
        );
        this.loaderService.hide();
      }
    });
    this.fileInput.nativeElement.value = '';
  }

  async convertHeicToJpeg(file: File): Promise<Blob> {
    // Only attempt conversion in browser environment
    if (!this.isBrowser) {
      throw new Error(
        'HEIC conversion is only available in browser environment'
      );
    }

    try {
      // Dynamic import to avoid SSR issues
      const heic2any = await import('heic2any');
      const convertedBlob = await heic2any.default({
        blob: file,
        toType: 'image/jpeg'
      });
      this.loaderService.hide();
      return convertedBlob as Blob;
    } catch (error: any) {
      this.loaderService.hide();
      console.error('Error converting HEIC to JPEG:', error);
      throw error;
    }
  }

  clearInput() {
    this.fileInput.nativeElement.value = '';
  }

  imageCropperLoad(src: string, file: File) {
    this.croppedImage = '';
    this.imageURL = src;
    this.croppingImageData = { name: file.name, type: file.type };
    this.displayImageCropper = true;
    this.showCropper = true;
    this.displayCropBtn = true;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  imageLoaded() {
    this.showCropper = true;
  }

  loadImageFailed() {
    this.clearInput();
  }

  zoomImgPreview(event: any) {
    if (event.deltaY > 0) {
      this.transform = {
        scale: parseFloat((this.transform.scale += 0.08).toFixed(2))
      };
    } else if (event.deltaY < 0 && this.transform.scale > 0.1) {
      this.transform = {
        scale: parseFloat((this.transform.scale -= 0.08).toFixed(2))
      };
    }
    if (this.transform.scale < this.minZoom) {
      this.transform = { scale: this.minZoom };
    }
    if (this.transform.scale > this.maxZoom) {
      this.transform = { scale: this.maxZoom };
    }
    this.zoomValue = this.transform.scale;
  }

  onZoom(event: any) {
    this.transform = { scale: event.value };
    this.zoomValue = event.value;
  }

  crop() {
    this.displayImageCropper = false;
    this.showCropper = false;
    this.displayCropBtn = false;

    if (this.croppedImage) {
      const fileImage = this.convertBase64ToFile(
        this.croppedImage,
        this.croppingImageData.name,
        this.croppingImageData.type
      );
      this.uploadFileWorkAround(fileImage, 0);
    } else {
      this.notificationService.showNotification(
        'ERROR',
        'Broken file content, please use another file'
      );
    }
  }

  toggleRoundCropper({ checked }: ToggleSwitchChangeEvent) {
    this.isRoundCropper = checked;
  }

  uploadVideoFile(fileForUpload: File) {
    this.businessService
      .createVideoAsset(isPlatformBrowser(this.platformId) ? window.origin : '')
      .subscribe({
        next: ({ data }) => {
          this.userCommonService
            .uploadMuxFile(data, fileForUpload)
            .pipe(
              takeWhile((response: any) => response?.status !== 'ready'), // Take responses until status is 'ready'
              takeUntil(this.destroy$)
            )
            .subscribe({
              next: () => {
                // for loader data
                this.uploadingVideoId = data.id;
              },
              complete: () => {
                this.fetchMuxVideoData();
              },
              error: () => {
                this.loaderService.hide();
                this.notificationService.showNotification(
                  'ERROR',
                  this.commonBindingService.getLabel('msg_video_upload_error')
                );
              }
            });
        },
        error: ({ message }) => {
          this.loaderService.hide();
          this.notificationService.showNotification('ERROR', message);
        }
      });
  }

  private convertBase64ToFile(
    base64String: string,
    fileName: string,
    mimeType: string
  ): File {
    const base64Index = base64String.indexOf(';base64,');
    const base64Content = base64String.substring(
      base64Index + ';base64,'.length
    );
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], fileName, { lastModified: Date.now() });
  }

  private uploadFileWorkAround(file: File, fileType: UploadFileTypesEnum) {
    return this.userCommonService
      .getUploadFileLink(file.name, fileType)
      .subscribe({
        next: ({ uploadFileUrl, assetId }) => {
          file = new File([file], assetId, { type: file.type });
          this.userCommonService.customPut(uploadFileUrl, file).subscribe({
            complete: () => {
              const fileUrl = uploadFileUrl.split('?')[0];
              this.onUpload({
                fileId: assetId,
                fileUrl,
                isRoundCropper: this.isRoundCropper
              });
            }
          });
        },
        error: () => {
          this.loaderService.hide();
          this.notificationService.showNotification(
            'ERROR',
            this.commonBindingService.getLabel('msg_file_upload_error')
          );
        }
      });
  }

  private fetchMuxVideoData() {
    this.loaderService.show();
    this.businessService.getVideoDetail(this.uploadingVideoId).subscribe({
      next: (metaDataResponse) => {
        if (metaDataResponse?.data?.status === 'ready') {
          this.loaderService.hide();
          return this.onUpload(metaDataResponse);
        } else {
          this.pollMuxAPI(4000);
        }
      },
      error: () => {
        this.loaderService.hide();
        this.notificationService.showNotification(
          'ERROR',
          this.commonBindingService.getLabel('msg_video_details_fetch_error')
        );
      }
    });
  }

  private pollMuxAPI(milli: number) {
    setTimeout(() => {
      this.fetchMuxVideoData();
    }, milli);
  }
}
